import type {
  BarChartInstrument,
  Instrument,
  InstrumentTag,
  InstrumentType,
  PresenceInstrument,
  SettingsControl,
  TableInstrument,
  TableInstrumentDataCell,
  TableInstrumentDataFormat,
} from 'lib-analytics-types';
import {
  getChartSeriesValue,
  isChartInstrument,
  isHeatmapInstrument,
  isMetricInstrument,
  isPresenceInstrument,
  isTableInstrument,
} from 'lib-analytics-ui';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import over from 'lodash/over';
import zipObject from 'lodash/zipObject';
import moment from 'moment-timezone';

type CSVRecord<TInstrumentType extends InstrumentType = InstrumentType> = {
  /** The type of the instrument the CSV data was generated from. */
  readonly instrumentType: TInstrumentType;
  /**
   * The CSV data, which is an array of CSV rows where each property represents
   * a column.
   */
  readonly data: Record<string, string | number | null>[];
  /** Optional metadata for the CSV record. */
  readonly meta?: {
    /** Optional warning message to display to the user. */
    readonly warning?: string;
  };
};

type TransformInstrumentFn<
  TInstrumentType extends InstrumentType = InstrumentType,
> = (
  instruments: readonly Instrument[],
  settings: SettingsControl,
) => CSVRecord<TInstrumentType>;

const dateFormat = 'YYYY-MM-DD HH:mm:ss';

export const transformInstrumentsToCSV = (
  ...args: Parameters<TransformInstrumentFn>
): CSVRecord[] =>
  over<CSVRecord>([
    transformMetricInstruments,
    transformChartInstrument,
    transformTableInstrument,
    transformPresenceInstrument,
  ])(...args).filter((record) => record?.data.length);

const transformMetricInstruments: TransformInstrumentFn<'metric'> = (
  instruments,
) => {
  const metricInstruments = instruments.filter(isMetricInstrument);

  return {
    instrumentType: 'metric',
    data: metricInstruments.reduce((data, instrument) => {
      if (!data[0]) {
        data.push({
          [instrument.title]: instrument.data,
        });
      } else {
        data[0][instrument.title] = instrument.data;
      }

      return data;
    }, [] as CSVRecord['data']),
  };
};

const transformChartInstrument: TransformInstrumentFn<'chart'> = (
  instruments,
  { timezone },
) => {
  const chartInstrument =
    instruments.find(isChartInstrument) ||
    instruments.find(isHeatmapInstrument);

  if (
    !chartInstrument ||
    !chartInstrument.data.categories.length ||
    !chartInstrument.data.series.length
  ) {
    return {
      instrumentType: 'chart',
      data: [],
    };
  }

  const {
    data: { categories, series },
  } = chartInstrument as BarChartInstrument;

  const hasDateCategories = moment(
    categories[0].label,
    moment.ISO_8601,
  ).isValid();

  return {
    instrumentType: 'chart',
    data: categories.map((category, categoryIndex) => ({
      [hasDateCategories ? 'Date' : '-']: hasDateCategories
        ? moment.tz(category.label, timezone).format(dateFormat)
        : category.label,
      ...series.reduce(
        (acc, item) => ({
          ...acc,
          [item.label]: getChartSeriesValue(item.data[categoryIndex]).value,
        }),
        {} as Record<string, number | null>,
      ),
    })),
  };
};

const transformPresenceInstrument: TransformInstrumentFn<'presence'> = (
  instruments,
) => {
  const presenceInstrument = instruments.find(isPresenceInstrument);

  if (!presenceInstrument) {
    return {
      instrumentType: 'presence',
      data: [],
    };
  }

  const {
    data: { availability, media_type, away },
  } = presenceInstrument as PresenceInstrument;

  const columns = ['Working (%)', 'Away (%)'].concat(
    away && away.length > 0 ? ['Away (ms)'] : [],
  );

  return {
    instrumentType: 'presence',
    data: [
      ...media_type.map((media) => ({
        _: media.label,
        ...zipObject(columns, [
          media.value,
          Math.max(100 - media.value, 0),
          null,
        ]),
      })),
      {
        _: 'Total',
        ...zipObject(columns, [
          availability[0]?.value,
          availability[1]?.value,
          null,
        ]),
      },
      ...(away && away.length > 0
        ? [
            {
              _: null,
              ...zipObject(columns, [null, null, null]),
            },
            ...away.map((item) => ({
              _: item.label,
              ...zipObject(columns, [null, null, item.value]),
            })),
          ]
        : []),
    ],
  };
};

const transformTableInstrument: TransformInstrumentFn<'table'> = (
  instruments,
  { timezone },
) => {
  const tableInstrument = instruments.find(isTableInstrument);

  if (!tableInstrument) {
    return {
      instrumentType: 'table',
      data: [],
    };
  }

  const {
    data: { columns, rows, options },
  } = tableInstrument as TableInstrument;

  return {
    instrumentType: 'table',
    data: rows.map((row) => ({
      ...columns.reduce(
        (acc, column, columnIndex) => ({
          ...acc,
          [column.label]: transformTableInstrumentDataValue(
            row.cells[columnIndex].value,
            column.format,
            timezone,
          ),
        }),
        {},
      ),
    })),
    meta: {
      warning: options?.next_page
        ? `Only the first ${options.limit} table results will be exported. Go back and click "Load all results" to export the full dataset.`
        : undefined,
    },
  };
};

const transformTableInstrumentDataValue = (
  value: TableInstrumentDataCell['value'],
  format: TableInstrumentDataFormat,
  timezone: string,
): string | number | null => {
  if (value === null) return null;

  switch (format) {
    case 'string':
    case 'number':
    case 'count':
    case 'percentage':
    case 'duration':
    case 'csid':
    case 'rating':
    case 'presence':
    case 'text':
      return value as string | number;

    case 'date':
      return moment.tz(value as string, timezone).format(dateFormat);

    case 'boolean':
      return (value as boolean) ? 'Y' : 'N';

    case 'tags':
      return (value as InstrumentTag[]).map(({ name }) => name).join('\n');

    case 'list':
      return (value as string[]).join('\n');

    // In case a new format is added to `TableInstrumentDataFormat` and this function is not
    // updated, a compile-time error will be thrown to ensure we handle all cases. If this however
    // happens at runtime, we log a warning and return null to ensure the export does not fail.
    default: {
      const unhandledFormat: never = format;

      Sentry.captureMessage(`Analytics: Unknown table instrument data format`, {
        level: 'warning',
        tags: {
          team: SentryTeam.Insights,
        },
        extra: { format: unhandledFormat },
      });

      return null;
    }
  }
};
